import React, { useState } from 'react';
import { PartAttribute, AttributeType, DrawingElement } from '@/app/types/cad';
import { Plus, Trash2, Edit2, Link } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface AttributeManagerProps {
  attributes: PartAttribute[];
  elements: DrawingElement[];
  onAttributeAdd: (attribute: Omit<PartAttribute, 'id'>) => void;
  onAttributeUpdate: (id: string, updates: Partial<PartAttribute>) => void;
  onAttributeDelete: (id: string) => void;
}

export function AttributeManager({
  attributes,
  elements,
  onAttributeAdd,
  onAttributeUpdate,
  onAttributeDelete,
}: AttributeManagerProps) {
  const { t } = useLanguage();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Local state for attribute value editing (prevents grid from disappearing on every keystroke)
  const [editingValueId, setEditingValueId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  
  const [newAttribute, setNewAttribute] = useState<{
    name: string;
    type: AttributeType;
    value: string | number | boolean;
    description: string;
  }>({
    name: '',
    type: 'string',
    value: '',
    description: '',
  });

  const handleAddAttribute = () => {
    if (!newAttribute.name.trim()) return;

    const attributeData: Omit<PartAttribute, 'id'> = {
      name: newAttribute.name.trim(),
      type: newAttribute.type,
      value: newAttribute.value,
      description: newAttribute.description.trim() || undefined,
      readonly: false,
    };

    // Add dnValues for DN reduction type
    if (newAttribute.type === 'dn-reduction') {
      attributeData.dnValues = { dn1: 100, dn2: 50 };
      attributeData.value = '100/50';
    }

    onAttributeAdd(attributeData);

    setNewAttribute({ name: '', type: 'string', value: '', description: '' });
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setNewAttribute({ name: '', type: 'string', value: '', description: '' });
    setIsAdding(false);
  };

  const validateAndFormatValue = (value: string, type: AttributeType): string | number | boolean => {
    switch (type) {
      case 'integer':
        const intVal = parseInt(value);
        return isNaN(intVal) ? 0 : intVal;
      case 'number':
        const numVal = parseFloat(value);
        return isNaN(numVal) ? 0 : numVal;
      case 'boolean':
        return value === 'true' || value === '1';
      case 'url':
      case 'email':
      case 'string':
      default:
        return value;
    }
  };

  const renderValueInput = (
    value: string | number | boolean,
    type: AttributeType,
    onChange: (value: string | number | boolean) => void,
    readonly: boolean = false,
    attr?: PartAttribute
  ) => {
    if (type === 'boolean') {
      return (
        <select
          value={value.toString()}
          onChange={(e) => onChange(e.target.value === 'true')}
          disabled={readonly}
          className="w-full px-2 py-1 text-sm border rounded"
          style={{
            borderColor: 'var(--md-outline-variant)',
            backgroundColor: readonly ? 'var(--md-surface-container-low)' : 'white',
          }}
        >
          <option value="true">{t('common.yes')}</option>
          <option value="false">{t('common.no')}</option>
        </select>
      );
    }

    // Special handling for DN type (common DN values)
    if (type === 'dn') {
      const commonDN = [10, 15, 20, 25, 32, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300, 350, 400, 500, 600, 700, 800, 900, 1000];
      return (
        <div className="flex gap-1">
          <select
            value={value.toString()}
            onChange={(e) => onChange(parseInt(e.target.value))}
            disabled={readonly}
            className="flex-1 px-2 py-1 text-sm border rounded"
            style={{
              borderColor: 'var(--md-outline-variant)',
              backgroundColor: readonly ? 'var(--md-surface-container-low)' : 'white',
            }}
          >
            {commonDN.map(dn => (
              <option key={dn} value={dn}>DN {dn}</option>
            ))}
          </select>
        </div>
      );
    }

    // Special handling for DN-reduction type (two DN values)
    if (type === 'dn-reduction' && attr) {
      const commonDN = [10, 15, 20, 25, 32, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300, 350, 400, 500, 600, 700, 800, 900, 1000];
      const dn1 = attr.dnValues?.dn1 || 100;
      const dn2 = attr.dnValues?.dn2 || 50;
      
      return (
        <div className="space-y-1">
          <div className="flex gap-1 items-center">
            <select
              value={dn1}
              onChange={(e) => {
                const newDn1 = parseInt(e.target.value);
                const id = attributes.find(a => a.id === attr.id)?.id;
                if (id) {
                  onAttributeUpdate(id, {
                    dnValues: { dn1: newDn1, dn2 },
                    value: `${newDn1}/${dn2}`
                  });
                }
              }}
              disabled={readonly}
              className="flex-1 px-2 py-1 text-sm border rounded"
              style={{
                borderColor: 'var(--md-outline-variant)',
                backgroundColor: readonly ? 'var(--md-surface-container-low)' : 'white',
              }}
            >
              {commonDN.map(dn => (
                <option key={dn} value={dn}>DN {dn}</option>
              ))}
            </select>
            <span className="text-sm font-bold" style={{ color: 'var(--md-on-surface-variant)' }}>/</span>
            <select
              value={dn2}
              onChange={(e) => {
                const newDn2 = parseInt(e.target.value);
                const id = attributes.find(a => a.id === attr.id)?.id;
                if (id) {
                  onAttributeUpdate(id, {
                    dnValues: { dn1, dn2: newDn2 },
                    value: `${dn1}/${newDn2}`
                  });
                }
              }}
              disabled={readonly}
              className="flex-1 px-2 py-1 text-sm border rounded"
              style={{
                borderColor: 'var(--md-outline-variant)',
                backgroundColor: readonly ? 'var(--md-surface-container-low)' : 'white',
              }}
            >
              {commonDN.map(dn => (
                <option key={dn} value={dn}>DN {dn}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-center font-mono" style={{ color: 'var(--md-on-surface-variant)' }}>
            DN {dn1}/{dn2}
          </div>
        </div>
      );
    }

    // Numeric types for TGA values
    const numericTypes = ['dn', 'insertion-depth', 'roughness', 'flow-rate', 'pressure-loss', 
                          'velocity', 'u-value', 'thermal-conductivity', 'wall-thickness', 
                          'insulation-thickness', 'temperature', 'pressure', 'power', 
                          'mass-flow', 'density', 'viscosity'];
    
    const isNumeric = numericTypes.includes(type);
    const inputType = type === 'integer' || isNumeric ? 'number' : 
                      type === 'email' ? 'email' :
                      type === 'url' ? 'url' : 'text';

    const step = type === 'integer' ? '1' : isNumeric ? 'any' : undefined;

    return (
      <div className="flex gap-1">
        <input
          type={inputType}
          value={attr && editingValueId === attr.id ? editingValue : value.toString()}
          onFocus={(e) => {
            if (attr) {
              setEditingValueId(attr.id);
              setEditingValue(value.toString());
            }
          }}
          onChange={(e) => {
            // Only update local state while editing
            if (attr && editingValueId === attr.id) {
              setEditingValue(e.target.value);
            } else if (!attr) {
              // For new attribute form, update immediately
              if (type === 'integer') {
                const val = e.target.value;
                if (val === '' || /^-?\d+$/.test(val)) {
                  onChange(val === '' ? 0 : parseInt(val));
                }
              } else if (isNumeric) {
                const val = e.target.value;
                if (val === '' || /^-?\d*\.?\d*$/.test(val)) {
                  onChange(val === '' ? 0 : parseFloat(val) || 0);
                }
              } else {
                onChange(e.target.value);
              }
            }
          }}
          onBlur={(e) => {
            // Commit changes on blur
            if (attr && editingValueId === attr.id) {
              let finalValue: string | number | boolean;
              
              if (type === 'integer') {
                const val = editingValue;
                finalValue = val === '' || !/^-?\d+$/.test(val) ? 0 : parseInt(val);
              } else if (isNumeric) {
                const val = editingValue;
                finalValue = val === '' || !/^-?\d*\.?\d*$/.test(val) ? 0 : parseFloat(val) || 0;
              } else {
                finalValue = editingValue;
              }
              
              onChange(finalValue);
              setEditingValueId(null);
              setEditingValue('');
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && attr && editingValueId === attr.id) {
              // Commit changes on Enter
              let finalValue: string | number | boolean;
              
              if (type === 'integer') {
                const val = editingValue;
                finalValue = val === '' || !/^-?\d+$/.test(val) ? 0 : parseInt(val);
              } else if (isNumeric) {
                const val = editingValue;
                finalValue = val === '' || !/^-?\d*\.?\d*$/.test(val) ? 0 : parseFloat(val) || 0;
              } else {
                finalValue = editingValue;
              }
              
              onChange(finalValue);
              setEditingValueId(null);
              setEditingValue('');
              e.currentTarget.blur();
            } else if (e.key === 'Escape' && attr && editingValueId === attr.id) {
              // Cancel editing on Escape
              setEditingValueId(null);
              setEditingValue('');
              e.currentTarget.blur();
            }
          }}
          step={step}
          readOnly={readonly}
          className="flex-1 px-2 py-1 text-sm border rounded"
          style={{
            borderColor: 'var(--md-outline-variant)',
            backgroundColor: readonly ? 'var(--md-surface-container-low)' : 'white',
          }}
        />
        {getAttributeUnit(type) && (
          <span className="px-2 py-1 text-xs rounded flex items-center" style={{
            backgroundColor: 'var(--md-surface-container-high)',
            color: 'var(--md-on-surface-variant)',
          }}>
            {attr?.unit || getAttributeUnit(type)}
          </span>
        )}
      </div>
    );
  };

  const getTypeLabel = (type: AttributeType): string => {
    const labels: Record<AttributeType, string> = {
      string: t('attributes.types.string'),
      number: t('attributes.types.number'),
      integer: t('attributes.types.integer'),
      boolean: t('attributes.types.boolean'),
      url: t('attributes.types.url'),
      email: t('attributes.types.email'),
      dimension: t('attributes.types.dimension'),
      angle: t('attributes.types.angle'),
      // TGA types
      'dn': t('attributes.types.dn'),
      'dn-reduction': t('attributes.types.dnReduction'),
      'insertion-depth': t('attributes.types.insertionDepth'),
      'roughness': t('attributes.types.roughness'),
      'flow-rate': t('attributes.types.flowRate'),
      'pressure-loss': t('attributes.types.pressureLoss'),
      'velocity': t('attributes.types.velocity'),
      'u-value': t('attributes.types.uValue'),
      'thermal-conductivity': t('attributes.types.thermalConductivity'),
      'wall-thickness': t('attributes.types.wallThickness'),
      'insulation-thickness': t('attributes.types.insulationThickness'),
      'temperature': t('attributes.types.temperature'),
      'pressure': t('attributes.types.pressure'),
      'power': t('attributes.types.power'),
      'mass-flow': t('attributes.types.massFlow'),
      'density': t('attributes.types.density'),
      'viscosity': t('attributes.types.viscosity'),
    };
    return labels[type];
  };

  // Get unit for attribute type
  const getAttributeUnit = (type: AttributeType): string => {
    const units: Record<AttributeType, string> = {
      'string': '',
      'integer': '',
      'number': '',
      'boolean': '',
      'email': '',
      'url': '',
      'date': '',
      'text': '',
      'dimension': 'mm',
      'angle': '°',
      'dn': 'DN',
      'dn-reduction': 'DN',
      'insertion-depth': 'mm',
      'roughness': 'mm',
      'flow-rate': 'm³/h',
      'pressure-loss': 'Pa',
      'velocity': 'm/s',
      'u-value': 'W/m²K',
      'thermal-conductivity': 'W/mK',
      'wall-thickness': 'mm',
      'insulation-thickness': 'mm',
      'temperature': '°C',
      'pressure': 'bar',
      'power': 'kW',
      'mass-flow': 'kg/h',
      'density': 'kg/m³',
      'viscosity': 'Pa·s',
    };
    return units[type] || '';
  };

  // Get dimension value for linked attributes
  const getDimensionValue = (dimensionId: string): string => {
    const dimension = elements.find(el => el.id === dimensionId);
    if (dimension && dimension.type === 'dimension') {
      return dimension.value;
    }
    return '';
  };

  // Get angle dimension value for linked attributes
  const getAngleDimensionValue = (angleDimensionId: string): string => {
    const angleDimension = elements.find(el => el.id === angleDimensionId);
    if (angleDimension && angleDimension.type === 'angle-dimension') {
      return angleDimension.value.toFixed(1);
    }
    return '';
  };

  return (
    <div className="flex flex-col h-full" data-tutorial="attribute-manager-btn">
      <div 
        className="p-3 flex items-center justify-between"
        style={{
          borderBottom: '1px solid var(--md-outline-variant)',
        }}
      >
        <h3 className="font-medium" style={{ color: 'var(--md-on-surface)' }}>
          {t('attributes.title')}
        </h3>
        <button
          onClick={() => setIsAdding(true)}
          className="p-1.5 rounded hover:bg-black/5 transition-colors"
          title={t('attributes.add')}
          data-tutorial="attribute-add-btn"
        >
          <Plus className="w-4 h-4" style={{ color: 'var(--md-primary)' }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3" data-tutorial="attribute-dialog">
        {/* Add new attribute form */}
        {isAdding && (
          <div 
            className="p-3 rounded border-2"
            style={{
              borderColor: 'var(--md-primary)',
              backgroundColor: 'var(--md-surface-container-lowest)',
            }}
          >
            <div className="space-y-2">
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                  {t('attributes.name')}
                </label>
                <input
                  type="text"
                  value={newAttribute.name}
                  onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                  placeholder={t('attributes.name')}
                  className="w-full px-2 py-1 text-sm border rounded"
                  style={{ borderColor: 'var(--md-outline-variant)' }}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                  {t('attributes.type')}
                </label>
                <select
                  value={newAttribute.type}
                  onChange={(e) => {
                    const type = e.target.value as AttributeType;
                    let defaultValue: string | number | boolean = '';
                    
                    // Set appropriate default values based on type
                    if (type === 'boolean') {
                      defaultValue = false;
                    } else if (type === 'dn') {
                      defaultValue = 50;
                    } else if (['integer', 'insertion-depth', 'roughness', 'flow-rate', 
                               'pressure-loss', 'velocity', 'u-value', 'thermal-conductivity', 
                               'wall-thickness', 'insulation-thickness', 'temperature', 
                               'pressure', 'power', 'mass-flow', 'density', 'viscosity'].includes(type)) {
                      defaultValue = 0;
                    }
                    
                    setNewAttribute({ 
                      ...newAttribute, 
                      type,
                      value: defaultValue
                    });
                  }}
                  className="w-full px-2 py-1 text-sm border rounded"
                  style={{ borderColor: 'var(--md-outline-variant)' }}
                >
                  <optgroup label={t('attributes.groups.general')}>
                    <option value="string">{t('attributes.types.string')}</option>
                    <option value="number">{t('attributes.types.number')}</option>
                    <option value="integer">{t('attributes.types.integer')}</option>
                    <option value="boolean">{t('attributes.types.boolean')}</option>
                    <option value="url">{t('attributes.types.url')}</option>
                    <option value="email">{t('attributes.types.email')}</option>
                  </optgroup>
                  <optgroup label={t('attributes.groups.tgaNominal')}>
                    <option value="dn">{t('attributes.types.dn')}</option>
                    <option value="dn-reduction">{t('attributes.types.dnReduction')}</option>
                  </optgroup>
                  <optgroup label={t('attributes.groups.tgaGeometry')}>
                    <option value="insertion-depth">{t('attributes.types.insertionDepth')}</option>
                    <option value="wall-thickness">{t('attributes.types.wallThickness')}</option>
                    <option value="insulation-thickness">{t('attributes.types.insulationThickness')}</option>
                  </optgroup>
                  <optgroup label={t('attributes.groups.tgaFlow')}>
                    <option value="flow-rate">{t('attributes.types.flowRate')}</option>
                    <option value="mass-flow">{t('attributes.types.massFlow')}</option>
                    <option value="velocity">{t('attributes.types.velocity')}</option>
                    <option value="pressure">{t('attributes.types.pressure')}</option>
                    <option value="pressure-loss">{t('attributes.types.pressureLoss')}</option>
                    <option value="roughness">{t('attributes.types.roughness')}</option>
                    <option value="viscosity">{t('attributes.types.viscosity')}</option>
                  </optgroup>
                  <optgroup label={t('attributes.groups.tgaThermic')}>
                    <option value="temperature">{t('attributes.types.temperature')}</option>
                    <option value="power">{t('attributes.types.power')}</option>
                    <option value="u-value">{t('attributes.types.uValue')}</option>
                    <option value="thermal-conductivity">{t('attributes.types.thermalConductivity')}</option>
                  </optgroup>
                  <optgroup label={t('attributes.groups.tgaPhysical')}>
                    <option value="density">{t('attributes.types.density')}</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                  {t('attributes.value')}
                </label>
                {renderValueInput(
                  newAttribute.value,
                  newAttribute.type,
                  (value) => setNewAttribute({ ...newAttribute, value })
                )}
              </div>

              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                  {t('attributes.description')} ({t('common.optional')})
                </label>
                <input
                  type="text"
                  value={newAttribute.description}
                  onChange={(e) => setNewAttribute({ ...newAttribute, description: e.target.value })}
                  placeholder={t('attributes.description')}
                  className="w-full px-2 py-1 text-sm border rounded"
                  style={{ borderColor: 'var(--md-outline-variant)' }}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleAddAttribute}
                  className="flex-1 px-3 py-1.5 rounded text-sm text-white"
                  style={{ backgroundColor: 'var(--md-primary)' }}
                >
                  {t('attributes.add')}
                </button>
                <button
                  onClick={handleCancelAdd}
                  className="flex-1 px-3 py-1.5 rounded text-sm"
                  style={{
                    backgroundColor: 'var(--md-surface-container-highest)',
                    color: 'var(--md-on-surface)',
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attribute list */}
        {attributes.length === 0 && !isAdding && (
          <div className="text-center py-8" style={{ color: 'var(--md-on-surface-variant)' }}>
            <p className="text-sm">{t('attributes.noAttributes')}</p>
            <p className="text-xs mt-1">{t('attributes.clickToAdd')}</p>
          </div>
        )}

        {attributes.map((attr) => (
          <div
            key={attr.id}
            className="p-3 rounded border"
            style={{
              borderColor: 'var(--md-outline-variant)',
              backgroundColor: attr.readonly ? 'var(--md-surface-container-low)' : 'var(--md-surface-container-lowest)',
            }}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                {editingId === attr.id ? (
                  <input
                    type="text"
                    value={attr.name}
                    onChange={(e) => onAttributeUpdate(attr.id, { name: e.target.value })}
                    onBlur={() => setEditingId(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setEditingId(null);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    className="px-2 py-0.5 text-sm font-medium border rounded w-full"
                    style={{ borderColor: 'var(--md-outline)' }}
                    autoFocus
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium" style={{ color: 'var(--md-on-surface)' }}>
                      {attr.name}
                    </h4>
                    {attr.dimensionId && (
                      <Link className="w-3 h-3" style={{ color: 'var(--md-primary)' }} title="Verknüpft mit Bemaßung" />
                    )}
                  </div>
                )}
                {/* Show original name if it exists and differs from current name */}
                {attr.originalName && attr.originalName !== attr.name && (
                  <div className="mt-0.5">
                    <span className="text-xs" style={{ color: 'var(--md-on-surface-variant)' }}>
                      {t('attributes.original')}: <span className="font-mono">{attr.originalName}</span>
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{
                    backgroundColor: 'var(--md-surface-container-high)',
                    color: 'var(--md-on-surface-variant)',
                  }}>
                    {getTypeLabel(attr.type)}
                  </span>
                  {attr.readonly && (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{
                      backgroundColor: 'var(--md-tertiary-container)',
                      color: 'var(--md-on-tertiary-container)',
                    }}>
                      {t('attributes.automatic')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditingId(attr.id)}
                  className="p-1 rounded hover:bg-black/5 transition-colors"
                  title={t('common.rename')}
                >
                  <Edit2 className="w-3 h-3" style={{ color: 'var(--md-on-surface-variant)' }} />
                </button>
                <button
                  onClick={() => onAttributeDelete(attr.id)}
                  className="p-1 rounded hover:bg-black/5 transition-colors"
                  title={t('common.delete')}
                >
                  <Trash2 className="w-3 h-3" style={{ color: 'var(--md-error)' }} />
                </button>
              </div>
            </div>

            {attr.description && (
              <p className="text-xs mb-2" style={{ color: 'var(--md-on-surface-variant)' }}>
                {attr.description}
              </p>
            )}

            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--md-on-surface-variant)' }}>
                {t('attributes.value')}
              </label>
              {attr.dimensionId ? (
                <input
                  type="text"
                  value={getDimensionValue(attr.dimensionId)}
                  readOnly
                  className="w-full px-2 py-1 text-sm border rounded"
                  style={{
                    borderColor: 'var(--md-outline-variant)',
                    backgroundColor: 'var(--md-surface-container-low)',
                  }}
                />
              ) : attr.angleDimensionId ? (
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={getAngleDimensionValue(attr.angleDimensionId)}
                    readOnly
                    className="flex-1 px-2 py-1 text-sm border rounded"
                    style={{
                      borderColor: 'var(--md-outline-variant)',
                      backgroundColor: 'var(--md-surface-container-low)',
                    }}
                  />
                  <span className="px-2 py-1 text-xs rounded flex items-center" style={{
                    backgroundColor: 'var(--md-surface-container-high)',
                    color: 'var(--md-on-surface-variant)',
                  }}>
                    °
                  </span>
                </div>
              ) : (
                renderValueInput(
                  attr.value,
                  attr.type,
                  (value) => onAttributeUpdate(attr.id, { value }),
                  attr.readonly,
                  attr
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <div 
        className="p-2 text-xs"
        style={{
          borderTop: '1px solid var(--md-outline-variant)',
          color: 'var(--md-on-surface-variant)',
          backgroundColor: 'var(--md-surface-container-low)',
        }}
      >
        {attributes.length} {attributes.length === 1 ? t('attributes.title').slice(0, -1) : t('attributes.title')}
      </div>
    </div>
  );
}